const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

/**
 * Jumps to `top` once the content is tall enough to reach it, so a restore
 * doesn't land short while the page is still rendering. Once the content
 * overflows and stops growing for `settleMs` (or after `maxWaitMs`), it
 * settles at the closest reachable position. Gives up as soon as the user
 * scrolls. Returns a function that cancels it.
 */
export const restoreWhenReachable = (
  container: HTMLElement,
  top: number,
  {
    settleMs = 2000,
    maxWaitMs = 10000,
  }: { settleMs?: number; maxWaitMs?: number } = {},
) => {
  let frame = 0;
  const startedAt = performance.now();
  let grewAt = startedAt;
  let lastHeight = container.scrollHeight;

  const maxScrollTop = () =>
    Math.max(0, container.scrollHeight - container.clientHeight);

  const jumpTo = (value: number) =>
    container.scrollTo({ top: value, behavior: 'instant' });

  const stop = () => {
    cancelAnimationFrame(frame);
    USER_SCROLL_EVENTS.forEach(event =>
      container.removeEventListener(event, stop),
    );
  };

  const attempt = () => {
    if (maxScrollTop() >= top) {
      jumpTo(top);
      return stop();
    }

    const now = performance.now();
    if (container.scrollHeight !== lastHeight) {
      lastHeight = container.scrollHeight;
      grewAt = now;
    }

    // A page that doesn't overflow yet is still behind a loading state
    const hasSettled = maxScrollTop() > 0 && now - grewAt >= settleMs;
    if (hasSettled || now - startedAt >= maxWaitMs) {
      jumpTo(Math.min(top, maxScrollTop()));
      return stop();
    }

    frame = requestAnimationFrame(attempt);
  };

  USER_SCROLL_EVENTS.forEach(event =>
    container.addEventListener(event, stop, { passive: true }),
  );

  attempt();

  return stop;
};
